import { useCallback, useEffect, useSyncExternalStore } from 'react';

// Memento pattern, contextual to each view.
//
// Every function of the console (Cost, Error, Log, Usage…) owns a state: filters, grouping, time
// window, selected tab. That state is the Originator. Each change produces a Memento — an
// immutable snapshot of the whole view state — kept by a Caretaker dedicated to that view, so:
//   - Annulla / Ripeti (toolbar, Ctrl+Z / Ctrl+Shift+Z) walk back and forth through the view's
//     own history and never touch another view;
//   - leaving a view and coming back restores it exactly as it was (the latest memento);
//   - the last memento survives a reload (localStorage, per browser), never shared, never sent.
// Typing in a text filter is coalesced into one memento, so undo does not go letter by letter.

interface Memento<S> {
  readonly state: S;
  readonly at: number;
  readonly label: string;
}

interface History<S> {
  past: Memento<S>[];
  present: Memento<S>;
  future: Memento<S>[];
  coalesceKey: string | null;
}

const LIMIT = 50;
const COALESCE_MS = 900;
const STORAGE = 'monitor.view.';

class Caretaker {
  private readonly history = new Map<string, History<unknown>>();
  private readonly listener = new Set<() => void>();
  active: string | null = null;
  private version = 0;

  subscribe = (listener: () => void) => {
    this.listener.add(listener);
    return () => this.listener.delete(listener);
  };

  snapshot = () => this.version;

  private emit() {
    this.version++;
    for (const listener of this.listener) listener();
  }

  ensure<S>(view: string, initial: S): History<S> {
    let entry = this.history.get(view) as History<S> | undefined;
    if (!entry) {
      entry = { past: [], present: { state: restore(view, initial), at: Date.now(), label: 'iniziale' }, future: [], coalesceKey: null };
      this.history.set(view, entry as History<unknown>);
    }
    return entry;
  }

  get<S>(view: string): History<S> | undefined {
    return this.history.get(view) as History<S> | undefined;
  }

  save<S>(view: string, next: S, label: string, coalesceKey: string | null) {
    const entry = this.history.get(view) as History<S> | undefined;
    if (!entry || Object.is(entry.present.state, next) || JSON.stringify(entry.present.state) === JSON.stringify(next)) return;
    const memento: Memento<S> = Object.freeze({ state: next, at: Date.now(), label });
    const merge = coalesceKey !== null && entry.coalesceKey === coalesceKey && memento.at - entry.present.at < COALESCE_MS;
    if (!merge) {
      entry.past.push(entry.present);
      if (entry.past.length > LIMIT) entry.past.shift();
    }
    entry.present = memento;
    entry.future = [];
    entry.coalesceKey = coalesceKey;
    persist(view, next);
    this.emit();
  }

  undo(view = this.active) {
    const entry = view ? this.history.get(view) : undefined;
    const previous = entry?.past.pop();
    if (!entry || !previous) return;
    entry.future.unshift(entry.present);
    entry.present = previous;
    entry.coalesceKey = null;
    persist(view!, previous.state);
    this.emit();
  }

  redo(view = this.active) {
    const entry = view ? this.history.get(view) : undefined;
    const next = entry?.future.shift();
    if (!entry || !next) return;
    entry.past.push(entry.present);
    entry.present = next;
    entry.coalesceKey = null;
    persist(view!, next.state);
    this.emit();
  }

  status(view = this.active) {
    const entry = view ? this.history.get(view) : undefined;
    return {
      view,
      canUndo: Boolean(entry?.past.length),
      canRedo: Boolean(entry?.future.length),
      undoLabel: entry?.present.label ?? null,
      redoLabel: entry?.future[0]?.label ?? null,
    };
  }

  setActive(view: string | null) {
    if (this.active === view) return;
    this.active = view;
    this.emit();
  }
}

export const caretaker = new Caretaker();

function restore<S>(view: string, initial: S): S {
  try {
    const raw = localStorage.getItem(STORAGE + view);
    if (!raw) return initial;
    const saved = JSON.parse(raw) as Partial<S>;
    // Only keys the view still knows: an old memento can never inject unexpected state.
    if (typeof initial !== 'object' || initial === null) return initial;
    const merged: Record<string, unknown> = { ...(initial as Record<string, unknown>) };
    for (const key of Object.keys(merged)) if (key in (saved as object)) merged[key] = (saved as Record<string, unknown>)[key];
    return merged as S;
  } catch {
    return initial;
  }
}

function persist(view: string, state: unknown) {
  try {
    localStorage.setItem(STORAGE + view, JSON.stringify(state));
  } catch {
    /* private mode or quota: the in-memory history still works */
  }
}

/**
 * State of one view, with its own undo/redo history. `seed` (e.g. from the URL) wins over the
 * stored memento for the keys it sets, so a shared link opens exactly what it says.
 */
export function useViewState<S extends object>(view: string, initial: S, seed?: Partial<S>) {
  const entry = caretaker.ensure(view, initial);
  useSyncExternalStore(caretaker.subscribe, caretaker.snapshot);

  useEffect(() => {
    caretaker.setActive(view);
    return () => {
      if (caretaker.active === view) caretaker.setActive(null);
    };
  }, [view]);

  const seedKey = seed ? JSON.stringify(seed) : '';
  useEffect(() => {
    if (!seed || Object.keys(seed).length === 0) return;
    const current = caretaker.get<S>(view)!.present.state;
    caretaker.save(view, { ...current, ...seed }, 'link', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, seedKey]);

  const set = useCallback(
    (patch: Partial<S> | ((state: S) => S), label = 'filtro') => {
      const current = caretaker.get<S>(view)!.present.state;
      const next = typeof patch === 'function' ? patch(current) : { ...current, ...patch };
      const keys = typeof patch === 'function' ? [] : Object.keys(patch);
      // Consecutive edits of the same single text field become one memento.
      const coalesce = keys.length === 1 && typeof (patch as Record<string, unknown>)[keys[0]!] === 'string' ? `${keys[0]}` : null;
      caretaker.save(view, next, label, coalesce);
    },
    [view],
  );

  const reset = useCallback(() => caretaker.save(view, initial, 'ripristino', null), [view, initial]);

  return [entry.present.state, set, reset] as const;
}

/** Undo/redo state of the view on screen, for the toolbar. */
export function useActiveMemento() {
  useSyncExternalStore(caretaker.subscribe, caretaker.snapshot);
  return { ...caretaker.status(), undo: () => caretaker.undo(), redo: () => caretaker.redo() };
}
